// src/hooks/useAthleteSearch.ts
// Custom hook that loads all athletes once from Firestore,
// then filters them locally as the coach types — instant, no extra reads.
//
// Backed by `getAthletesAggregate()`, which reads a single pre-built
// `aggregates/athletes` document (maintained by the Cloud Function
// `maintainAthletesAggregate`). One read regardless of athlete count.

import { useState, useEffect, useMemo, useRef } from "react";
import { AthleteAggregate, getAthletesAggregate, subscribeAthleteAggregateChanges } from "../firebase/athletes";
import { useAuth } from "../context/AuthContext";
import { getCoachOrganizationAccess, getCurrentAthleteMemberships } from "../firebase/organizationFoundation";

// The shape of a single athlete from Firestore
export interface Athlete {
  id: string;          // Firestore document ID
  athleteId: string;   // Federation ID e.g. "GEO-1234"
  firstName: string;
  lastName: string;
  dateOfBirth: string; // "YYYY-MM-DD"
  club?: string;       // Club name (optional)
  status?: "ACTIVE" | "INACTIVE" | "ARCHIVED";
  nationality?: string;
  competitorType?: "FEDERATION_MEMBER" | "EXTERNAL_COMPETITOR";
  rankingEligible?: boolean;
}

interface UseAthleteSearchResult {
  athletes: Athlete[];          // full list (all athletes loaded)
  filtered: Athlete[];          // filtered list shown in search results
  search: string;               // current search query string
  setSearch: (q: string) => void; // call this when the input changes
  loading: boolean;             // true while initial load is happening
}

export function toSearchAthlete(athlete: AthleteAggregate): Athlete {
  return {
    id: athlete.id,
    athleteId: athlete.athleteId ?? "",
    firstName: athlete.firstName ?? "",
    lastName: athlete.lastName ?? "",
    dateOfBirth: athlete.dateOfBirth ?? "",
    club: athlete.club,
    status: athlete.status,
    nationality: athlete.nationality,
    competitorType: athlete.competitorType,
    rankingEligible: athlete.rankingEligible,
  };
}

/**
 * Loads the full athletes collection once on mount, then filters
 * locally based on the search query. Searches by:
 *   - Federation ID (athleteId) — partial match
 *   - Full name (firstName + lastName) — case-insensitive partial match
 */
export function useAthleteSearch(): UseAthleteSearchResult {
  const { currentUser } = useAuth();
  const [athletes, setAthletes] = useState<Athlete[]>([]);
  const [search, setSearchRaw] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);
  const permittedAthleteIds = useRef<Set<string> | null>(null);

  // Load the athletes aggregate once when the hook mounts
  useEffect(() => {
    Promise.all([
      getAthletesAggregate(),
      currentUser ? getCoachOrganizationAccess(currentUser.uid).catch(() => null) : Promise.resolve(null),
    ])
      .then(async ([data, access]) => {
        let mapped: Athlete[] = data.map(toSearchAthlete);
        const organizationIds = access ? [...new Set([
          ...access.rosterOrganizationIds,
          ...access.registrationOrganizationIds,
          ...access.regionalRegistrationOrganizationIds,
        ])] : [];
        // Authoritative mode is activated by any projected organization grant.
        // Legacy federation-wide fallback is used only while that projection is
        // absent/empty (or unreadable) during production reconciliation.
        if (organizationIds.length) {
          const memberships = await getCurrentAthleteMemberships(organizationIds);
          const permittedIds = new Set(memberships.map((membership) => membership.athleteId));
          permittedAthleteIds.current = permittedIds;
          mapped = mapped.filter((athlete) => permittedIds.has(athlete.athleteId));
        } else {
          permittedAthleteIds.current = null;
        }
        setAthletes(mapped);
      })
      .catch((err) => {
        console.error("Failed to load athletes:", err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [currentUser]);

  useEffect(() => subscribeAthleteAggregateChanges((created) => {
    if (permittedAthleteIds.current && !permittedAthleteIds.current.has(created.athleteId)) return;
    setAthletes((current) => {
      const next = current.filter((athlete) => athlete.id !== created.id && athlete.athleteId !== created.athleteId);
      next.push({ ...created });
      return next;
    });
  }), []);

  // Re-filter every time the search string changes.
  //
  // Tokenized + ranked: the query is split on whitespace and every token must
  // appear somewhere in the first name, last name, full name, or federation ID.
  // This makes partial/reversed queries work ("doe jo", "jo doe", "GEO-12"),
  // and results are ranked so prefix/name matches surface first. Capped at 50
  // so the dropdown stays light on phones.
  const filtered = useMemo(() => {
    const q = search;
    const tokens = q.toLowerCase().trim().split(/\s+/).filter(Boolean);
    if (tokens.length === 0) return [];

    const scored: Array<{ a: Athlete; score: number }> = [];
    for (const a of athletes) {
      if (a.status === "ARCHIVED") continue;
      const fn = a.firstName.toLowerCase();
      const ln = a.lastName.toLowerCase();
      const id = a.athleteId.toLowerCase();
      const full = `${fn} ${ln}`;
      const rev = `${ln} ${fn}`;

      // Every token must hit one of the fields, else it's not a match.
      let matchesAll = true;
      for (const tk of tokens) {
        if (!(fn.includes(tk) || ln.includes(tk) || id.includes(tk) || full.includes(tk))) {
          matchesAll = false;
          break;
        }
      }
      if (!matchesAll) continue;

      const joined = tokens.join(" ");
      let score = 0;
      if (full.startsWith(joined) || rev.startsWith(joined)) score += 100;
      if (fn.startsWith(tokens[0]) || ln.startsWith(tokens[0])) score += 50;
      if (id.startsWith(tokens[0])) score += 40;
      if (full.includes(joined)) score += 10;
      scored.push({ a, score });
    }

    scored.sort(
      (x, y) =>
        y.score - x.score ||
        `${x.a.lastName} ${x.a.firstName}`.localeCompare(`${y.a.lastName} ${y.a.firstName}`)
    );

    return scored.slice(0, 50).map((x) => x.a);
  }, [athletes, search]);

  const setSearch = (q: string) => setSearchRaw(q);

  return { athletes, filtered, search, setSearch, loading };
}
